import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CurrencyProvider } from "./contexts/CurrencyContext";
import ErrorBoundary from "./components/ErrorBoundary";
import Layout from "./components/Layout";
import EmbeddedBridge from "./components/EmbeddedBridge";
import { Suspense, lazy } from "react";
import { RefreshCw } from "lucide-react";

// ── Command Centre v3 (sole hub) ────────────────────────────────────────
const CommandCentre = lazy(() => import("./pages/CommandCentre"));

// ── Sidebar "More" — 3 groups, 14 pages ─────────────────────────────────
// Financials
const FinanceCFO = lazy(() => import("./pages/FinanceCFO"));
const COGSManagement = lazy(() => import("./pages/COGSManagement"));
const ReportCenter = lazy(() => import("./pages/ReportCenter"));
// Growth
const ProductsTab = lazy(() => import("./pages/ProductsTab"));
const PricingTab = lazy(() => import("./pages/PricingTab"));
const CampaignsTab = lazy(() => import("./pages/CampaignsTab"));
const Retention = lazy(() => import("./pages/Retention"));
const CompetitorAnalysis = lazy(() => import("./pages/CompetitorAnalysis"));
const CRO = lazy(() => import("./pages/CRO"));
const SEOTab = lazy(() => import("./pages/SEOTab"));
// Data & Automation
const Analytics = lazy(() => import("./pages/Analytics"));
const Attribution = lazy(() => import("./pages/Attribution"));
const InventoryAware = lazy(() => import("./pages/InventoryAware"));
const Autopilot = lazy(() => import("./pages/Autopilot"));
const HistoryTab = lazy(() => import("./pages/HistoryTab"));
const Results = lazy(() => import("./pages/Results"));
const MarketingHub = lazy(() => import("./pages/MarketingHub"));
const ActivationFlow = lazy(() => import("./pages/ActivationFlow"));

// ── Bottom nav ──────────────────────────────────────────────────────────
const IntegrationsSettings = lazy(() => import("./pages/IntegrationsSettings"));
const IntegrationsSettingsLegacy = lazy(() => import("./pages/IntegrationsSettingsLegacy"));
const ConnectorsHub = lazy(() => import("./pages/ConnectorsHub"));
const Settings = lazy(() => import("./pages/Settings"));
const Help = lazy(() => import("./pages/Help"));

// ── Public / auth ───────────────────────────────────────────────────────
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const AppStoreListing = lazy(() => import("./pages/AppStoreListing"));
const PublicDemo = lazy(() => import("./pages/PublicDemo"));
const PricingPublic = lazy(() => import("./pages/Pricing"));
const Compare = lazy(() => import("./pages/Compare"));
const Share = lazy(() => import("./pages/Share"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const Tools = lazy(() => import("./pages/Tools"));
const ShopifyProfitCalculator = lazy(() => import("./pages/tools/ShopifyProfitCalculator"));
const ROASCalculator = lazy(() => import("./pages/tools/ROASCalculator"));
const MarginCalculator = lazy(() => import("./pages/tools/MarginCalculator"));
const BreakEvenCPACalculator = lazy(() => import("./pages/tools/BreakEvenCPACalculator"));
const BundleCalculator = lazy(() => import("./pages/tools/BundleCalculator"));
const ShippingCalculator = lazy(() => import("./pages/tools/ShippingCalculator"));

function PageLoader() {
  return <div className="min-h-[40vh] flex items-center justify-center"><RefreshCw className="w-5 h-5 animate-spin text-emerald-600" /></div>;
}

function ProtectedRoute({ children }) {
  const { loading } = useAuth();
  if (loading) return <PageLoader />;
  return children;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/tools" element={<Tools />} />
        <Route path="/tools/shopify-profit-calculator" element={<ShopifyProfitCalculator />} />
        <Route path="/tools/roas-calculator" element={<ROASCalculator />} />
        <Route path="/tools/margin-calculator" element={<MarginCalculator />} />
        <Route path="/tools/break-even-cpa" element={<BreakEvenCPACalculator />} />
        <Route path="/tools/bundle-calculator" element={<BundleCalculator />} />
        <Route path="/tools/shipping-calculator" element={<ShippingCalculator />} />
        <Route path="/public-demo" element={<PublicDemo />} />
        <Route path="/app-store" element={<AppStoreListing />} />
        <Route path="/pricing-page" element={<PricingPublic />} />
        <Route path="/compare" element={<Compare />} />
        <Route path="/share/:id" element={<Share />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/onboarding" element={<Navigate to="/" replace />} />

        {/* Authenticated app */}
        <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route index element={<CommandCentre />} />
          <Route path="command-centre" element={<CommandCentre />} />

          {/* Financials */}
          <Route path="finance" element={<FinanceCFO />} />
          <Route path="cogs-management" element={<COGSManagement />} />
          <Route path="report-center" element={<ReportCenter />} />

          {/* Growth */}
          <Route path="products-view" element={<ProductsTab />} />
          <Route path="smart-pricing" element={<PricingTab />} />
          {/* Iter 176 — Marketing hub with SEO, SEM, Email, Social, CRO, Content Studio subtabs */}
          <Route path="marketing" element={<MarketingHub />} />
          <Route path="marketing/:tab" element={<MarketingHub />} />
          <Route path="retention" element={<Retention />} />
          <Route path="buyers" element={<Navigate to="/retention" replace />} />
          <Route path="competitor-analysis" element={<CompetitorAnalysis />} />
          {/* Iter 176 — legacy standalone /cro and /seo now redirect into the Marketing hub */}
          <Route path="cro" element={<Navigate to="/marketing/cro" replace />} />
          <Route path="seo" element={<Navigate to="/marketing/seo" replace />} />
          <Route path="campaigns-view" element={<Navigate to="/marketing" replace />} />

          {/* Data & Automation */}
          <Route path="analytics" element={<Analytics />} />
          <Route path="attribution" element={<Attribution />} />
          <Route path="inventory-aware" element={<InventoryAware />} />
          <Route path="autopilot" element={<Autopilot />} />
          <Route path="history" element={<HistoryTab />} />
          <Route path="results" element={<Results />} />
          <Route path="activate" element={<ActivationFlow />} />

          {/* Bottom nav */}
          <Route path="integrations-settings" element={<IntegrationsSettings />} />
          <Route path="integrations-oauth" element={<IntegrationsSettingsLegacy />} />
          <Route path="connectors" element={<ConnectorsHub />} />
          <Route path="settings" element={<Settings />} />
          <Route path="help" element={<Help />} />

          {/* Legacy aliases → route to canonical destinations */}
          <Route path="command-center" element={<Navigate to="/" replace />} />
          <Route path="legacy-command-center" element={<Navigate to="/" replace />} />
          <Route path="dashboard" element={<Navigate to="/" replace />} />
          <Route path="details" element={<Navigate to="/" replace />} />
          <Route path="pricing" element={<Navigate to="/smart-pricing" replace />} />
          <Route path="pricing/*" element={<Navigate to="/smart-pricing" replace />} />
          <Route path="products" element={<Navigate to="/products-view" replace />} />
          <Route path="products/*" element={<Navigate to="/products-view" replace />} />
          <Route path="apps-audit" element={<Navigate to="/marketing/seo" replace />} />
          <Route path="reports/traffic" element={<Navigate to="/analytics" replace />} />
          <Route path="reports/mobile" element={<Navigate to="/analytics" replace />} />
          <Route path="trust" element={<Navigate to="/retention" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <CurrencyProvider>
              <EmbeddedBridge />
              <AppRoutes />
              <Toaster position="top-right" />
            </CurrencyProvider>
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
